/**
 * Pricing Calculator
 *
 * Power-law pricing:
 *   Raw price per user = 30 / (users^0.25938375)
 *
 * Per-user price is rounded to 3 decimal places (tenths of a penny) and
 * then multiplied by the user count to derive the monthly total. This
 * removes the rounding artefacts that occurred when totals were rounded
 * to whole pounds (which previously caused adding a user to lower the
 * bill at some boundaries).
 *
 * For users > 1000:
 *   Total = tier-1 total at exactly 1000 users + (n - 1000) × £5.
 *
 * Display convention: per-user is shown to 2 decimal places; monthly
 * total is shown to 2 decimal places.
 */

const POWER_LAW_EXPONENT = 0.25938375;
const BASE_PRICE = 30;
const TIER_1_MAX = 1000;
const TIER_2_RATE = 5; // £5 per user above 1000

function round3(value: number): number {
  return Math.round(value * 1000) / 1000;
}

/** Per-user price (£/month) rounded to 3 decimal places. */
export function calculatePricePerUser(userCount: number): number {
  if (userCount < 1) return 0;
  if (userCount <= TIER_1_MAX) {
    return round3(BASE_PRICE / Math.pow(userCount, POWER_LAW_EXPONENT));
  }
  return calculateMonthlyPrice(userCount) / userCount;
}

/** Monthly total (£) - exact to tenths of a penny. */
export function calculateMonthlyPrice(userCount: number): number {
  if (userCount < 1) return 0;
  if (userCount <= TIER_1_MAX) {
    return round3(BASE_PRICE / Math.pow(userCount, POWER_LAW_EXPONENT)) * userCount;
  }
  const perUserAt1000 = round3(BASE_PRICE / Math.pow(TIER_1_MAX, POWER_LAW_EXPONENT));
  const tier1Total = perUserAt1000 * TIER_1_MAX;
  return tier1Total + (userCount - TIER_1_MAX) * TIER_2_RATE;
}

export function calculateAnnualPrice(userCount: number): number {
  return calculateMonthlyPrice(userCount) * 12;
}

export function calculateSavingsPercentage(userCount: number): number {
  if (userCount <= 1) return 0;
  const singleUserPricePerUser = calculatePricePerUser(1);
  const currentPricePerUser = calculatePricePerUser(userCount);
  return ((singleUserPricePerUser - currentPricePerUser) / singleUserPricePerUser) * 100;
}

export interface PriceBreakdown {
  userCount: number;
  monthlyTotal: number;
  monthlyPerUser: number;
  annualTotal: number;
  savingsPercentage: number;
  requiresContact: boolean;
}

export function formatPriceBreakdown(userCount: number): PriceBreakdown {
  return {
    userCount,
    monthlyTotal: calculateMonthlyPrice(userCount),
    monthlyPerUser: calculatePricePerUser(userCount),
    annualTotal: calculateAnnualPrice(userCount),
    savingsPercentage: calculateSavingsPercentage(userCount),
    requiresContact: false,
  };
}

export function getPricingTiers() {
  const tiers = [1, 5, 10, 25, 50, 100, 250, 500, 750, 1000];
  return tiers.map(userCount => ({
    users: userCount,
    monthlyTotal: calculateMonthlyPrice(userCount),
    monthlyPerUser: calculatePricePerUser(userCount),
    annualTotal: calculateAnnualPrice(userCount),
    savingsPercentage: calculateSavingsPercentage(userCount),
  }));
}

/**
 * Pick a chart x-axis maximum that scales sensibly with the current
 * user count, so the scale only changes when the user crosses a step.
 */
export function getChartMaxForUserCount(userCount: number): number {
  if (userCount <= 50) return 50;
  if (userCount <= 100) return 100;
  if (userCount <= 250) return 250;
  if (userCount <= 500) return 500;
  return 1000;
}

/**
 * Build a data point per integer user count up to `max`, so the chart
 * has a value at every individual user position.
 */
export function getChartDataPoints(max: number = 50): Array<{ users: number; pricePerUser: number }> {
  const points: Array<{ users: number; pricePerUser: number }> = [];
  for (let i = 1; i <= max; i++) {
    points.push({ users: i, pricePerUser: calculatePricePerUser(i) });
  }
  return points;
}

export function isValidUserCount(count: number): boolean {
  return count >= 1 && Number.isInteger(count);
}

export function getTierInfo(userCount: number): {
  tier: number;
  description: string;
  rate?: string;
} {
  if (userCount <= TIER_1_MAX) {
    return {
      tier: 1,
      description: 'Power-law pricing (best value)',
      rate: 'Variable, decreasing per user',
    };
  }
  return {
    tier: 2,
    description: 'Extended tier',
    rate: `£${TIER_2_RATE.toFixed(2)} per additional user`,
  };
}
